import {
  GoogleDriveDocumentExtension,
  GoogleDriveFile,
  GoogleDriveImageExtension,
  GoogleDriveVideoExtension,
} from 'types';

// Video file extensions
const VIDEO_EXTENSIONS = [
  '.mp4',
  '.mov',
  '.avi',
  '.wmv',
  '.flv',
  '.webm',
  '.mkv',
  '.m4v',
  '.3gp',
  '.ogv',
];

// Image file extensions
const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.webp',
  '.svg',
  '.tiff',
  '.tif',
];

/**
 * Detects if a file is a video based on its URL or file extension
 */
export function isVideoFile(fileUrl: string): boolean {
  if (!fileUrl) return false;

  const url = fileUrl.toLowerCase();
  return VIDEO_EXTENSIONS.some((ext) => url.includes(ext));
}

/**
 * Detects if a file is an image based on its URL or file extension
 */
export function isImageFile(fileUrl: string): boolean {
  if (!fileUrl) return false;

  const url = fileUrl.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => url.includes(ext));
}

/**
 * Separates media files into images and videos
 */
export function separateMediaFiles(files: any[]): {
  images: any[];
  videos: any[];
} {
  const images: any[] = [];
  const videos: any[] = [];

  files.forEach((file) => {
    // Check the file URL or thumbnail URL to determine type
    const fileUrl = file.file || file.large || file.medium || file.thumbnail;

    if (isVideoFile(fileUrl)) {
      videos.push(file);
    } else if (isImageFile(fileUrl)) {
      images.push(file);
    } else {
      // Default to image if type cannot be determined
      images.push(file);
    }
  });

  return {images, videos};
}

const GOOGLE_DRIVE_IMAGE_EXTENSIONS: GoogleDriveImageExtension[] = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
];

const GOOGLE_DRIVE_VIDEO_EXTENSIONS: GoogleDriveVideoExtension[] = [
  'mp4',
  'mov',
  'avi',
  'mkv',
  'webm',
];

const GOOGLE_DRIVE_DOCUMENT_EXTENSIONS: GoogleDriveDocumentExtension[] = [
  'pdf',
  'txt',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
];

export function isGoogleDriveImageExtension(extension: string): boolean {
  return GOOGLE_DRIVE_IMAGE_EXTENSIONS.includes(
    extension as GoogleDriveImageExtension
  );
}

export function isGoogleDriveVideoExtension(extension: string): boolean {
  return GOOGLE_DRIVE_VIDEO_EXTENSIONS.includes(
    extension as GoogleDriveVideoExtension
  );
}

export function isGoogleDriveDocumentExtension(extension: string): boolean {
  return GOOGLE_DRIVE_DOCUMENT_EXTENSIONS.includes(
    extension as GoogleDriveDocumentExtension
  );
}

/**
 * Separates google drive files into images, videos and documents
 */
export function separateGoogleDriveFiles(files: GoogleDriveFile[]): {
  images: GoogleDriveFile[];
  videos: GoogleDriveFile[];
  documents: GoogleDriveFile[];
} {
  const images: GoogleDriveFile[] = [];
  const videos: GoogleDriveFile[] = [];
  const documents: GoogleDriveFile[] = [];

  files.forEach((file) => {
    if (
      isGoogleDriveImageExtension(
        file.filename.split('.').pop() as GoogleDriveImageExtension
      )
    ) {
      images.push(file);
    } else if (
      isGoogleDriveVideoExtension(
        file.filename.split('.').pop() as GoogleDriveVideoExtension
      )
    ) {
      videos.push(file);
    } else if (
      isGoogleDriveDocumentExtension(
        file.filename.split('.').pop() as GoogleDriveDocumentExtension
      )
    ) {
      documents.push(file);
    }
  });

  return {images, videos, documents};
}
